import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Alert, LinearProgress,
  Stack, Chip, Divider, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem,
} from '@mui/material';
import {
  Business as OrgIcon,
  Edit as EditIcon,
  Email as ResendIcon,
  Download as ExportIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { tenantAdminApi } from '../api/services';
import { useAuth } from '../auth/AuthProvider';

const COLORS = {
  primary: '#6C9BFF',
  secondary: '#FF8A65',
  success: '#69F0AE',
  error: '#FF5252',
  warning: '#FFB74D',
  bgPaper: '#121829',
  textPrimary: '#E8ECF4',
  textSecondary: '#8892A8',
};

interface TenantInfo {
  id: string;
  name: string;
  domain?: string;
  status: string;
  plan: string;
  maxExecutiveSeats: number;
  maxEaSeats: number;
  currentExecutives: number;
  currentEAs: number;
  totalUsers: number;
  aiPlanningEnabled: boolean;
  transcriptProcessingEnabled: boolean;
  imageAnalysisEnabled: boolean;
  pdfExportEnabled: boolean;
  webhooksEnabled: boolean;
  apiAccessEnabled: boolean;
  monthlyAiTokenLimit: number;
  monthlyTranscriptLimit: number;
  trialExpiresAt?: string;
  subscriptionExpiresAt?: string;
}

interface TenantUser {
  id: string;
  email: string;
  displayName: string;
  role: string;
  isActive: boolean;
  createdAt: string;
  lastLoginAt: string | null;
}

const VALID_ROLES = ['Executive', 'ExecutiveAssistant', 'Admin', 'TenantAdmin'];

function SeatUsageBar({ label, current, max }: { label: string; current: number; max: number }) {
  const percent = max > 0 ? Math.min((current / max) * 100, 100) : 0;
  const isHigh = percent > 80;
  const isFull = percent >= 100;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="body2" sx={{ color: COLORS.textPrimary, fontWeight: 500 }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ color: isFull ? COLORS.error : isHigh ? COLORS.warning : COLORS.textSecondary }}>
          {current} of {max} seats
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={percent}
        sx={{
          height: 8,
          borderRadius: 4,
          bgcolor: 'rgba(255,255,255,0.06)',
          '& .MuiLinearProgress-bar': {
            bgcolor: isFull ? COLORS.error : isHigh ? COLORS.warning : COLORS.primary,
            borderRadius: 4,
          },
        }}
      />
    </Box>
  );
}

function FeatureFlag({ label, enabled }: { label: string; enabled: boolean }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      {enabled
        ? <CheckIcon sx={{ fontSize: 18, color: COLORS.success }} />
        : <CancelIcon sx={{ fontSize: 18, color: COLORS.textSecondary, opacity: 0.5 }} />}
      <Typography variant="body2" sx={{ color: enabled ? COLORS.textPrimary : COLORS.textSecondary }}>
        {label}
      </Typography>
    </Stack>
  );
}

export default function TenantOrganisationPage() {
  const { user } = useAuth();
  const [tenant, setTenant] = useState<TenantInfo | null>(null);
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [exportLoading, setExportLoading] = useState(false);

  // Edit user dialog
  const [editUser, setEditUser] = useState<TenantUser | null>(null);
  const [editRole, setEditRole] = useState('');
  const [editActive, setEditActive] = useState(true);
  const [editSaving, setEditSaving] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [tenantResult, usersResult] = await Promise.allSettled([
        tenantAdminApi.getTenant(),
        tenantAdminApi.getUsers(),
      ]);

      if (tenantResult.status === 'fulfilled') setTenant(tenantResult.value);
      if (usersResult.status === 'fulfilled') setUsers(usersResult.value);

      if (tenantResult.status === 'rejected' && usersResult.status === 'rejected') {
        setError('Failed to load organisation data.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleEditOpen = (u: TenantUser) => {
    setEditUser(u);
    setEditRole(u.role);
    setEditActive(u.isActive);
  };

  const handleEditSave = async () => {
    if (!editUser) return;
    setEditSaving(true);
    setError(null);
    try {
      if (editRole !== editUser.role) {
        await tenantAdminApi.updateUserRole(editUser.id, editRole);
      }
      if (editActive !== editUser.isActive) {
        await tenantAdminApi.updateUserStatus(editUser.id, editActive);
      }
      setEditUser(null);
      setSuccess('User updated successfully.');
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to update user.');
    } finally {
      setEditSaving(false);
    }
  };

  const handleResendWelcome = async (userId: string) => {
    setError(null);
    try {
      const result = await tenantAdminApi.resendWelcomeEmail(userId);
      setSuccess(result.message || 'Welcome email sent.');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to send welcome email.');
    }
  };

  const handleExport = async () => {
    setExportLoading(true);
    setError(null);
    try {
      const response = await tenantAdminApi.exportData();
      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const disposition = response.headers?.['content-disposition'];
      let filename = 'cadence-export.json';
      if (disposition) {
        const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match?.[1]) filename = match[1].replace(/['"]/g, '');
      }
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setSuccess('Data export downloaded successfully.');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to export data.');
    } finally {
      setExportLoading(false);
    }
  };

  const statusColor = (status: string) => {
    switch (status) {
      case 'Active': return 'success';
      case 'Trialing': case 'Trial': return 'info';
      case 'Suspended': return 'warning';
      case 'Cancelled': return 'error';
      default: return 'default';
    }
  };

  const roleColor = (role: string) => {
    switch (role) {
      case 'Admin': case 'TenantAdmin': return 'error';
      case 'Executive': return 'primary';
      case 'ExecutiveAssistant': return 'secondary';
      default: return 'default';
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <OrgIcon sx={{ color: 'primary.main', fontSize: 28 }} />
        <Box>
          <Typography variant="h4">My Organisation</Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Manage your organisation, users, and view plan details
          </Typography>
        </Box>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Grid container spacing={2.5}>
        {/* Organisation Info Card */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ bgcolor: COLORS.bgPaper, height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: COLORS.textPrimary, fontWeight: 600, mb: 2 }}>
                Organisation Info
              </Typography>
              {tenant ? (
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="caption" sx={{ color: COLORS.textSecondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Name
                    </Typography>
                    <Typography variant="body1" sx={{ color: COLORS.textPrimary, fontWeight: 500 }}>
                      {tenant.name}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={2}>
                    <Box>
                      <Typography variant="caption" sx={{ color: COLORS.textSecondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Plan
                      </Typography>
                      <Typography variant="body1" sx={{ color: COLORS.primary, fontWeight: 600 }}>
                        {tenant.plan}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: COLORS.textSecondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Status
                      </Typography>
                      <Box sx={{ mt: 0.25 }}>
                        <Chip label={tenant.status} size="small" color={statusColor(tenant.status) as any} sx={{ fontWeight: 600 }} />
                      </Box>
                    </Box>
                  </Stack>
                  {tenant.subscriptionExpiresAt && (
                    <Box>
                      <Typography variant="caption" sx={{ color: COLORS.textSecondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Subscription Expires
                      </Typography>
                      <Typography variant="body2" sx={{ color: COLORS.textPrimary }}>
                        {new Date(tenant.subscriptionExpiresAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </Typography>
                    </Box>
                  )}
                  {tenant.domain && (
                    <Box>
                      <Typography variant="caption" sx={{ color: COLORS.textSecondary, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Domain
                      </Typography>
                      <Typography variant="body2" sx={{ color: COLORS.textPrimary }}>
                        {tenant.domain}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              ) : !loading ? (
                <Typography variant="body2" sx={{ color: COLORS.textSecondary, py: 3, textAlign: 'center' }}>
                  Unable to load organisation details.
                </Typography>
              ) : null}
            </CardContent>
          </Card>
        </Grid>

        {/* Seat Usage Card */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={{ bgcolor: COLORS.bgPaper, height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: COLORS.textPrimary, fontWeight: 600, mb: 2 }}>
                Seat Usage
              </Typography>
              {tenant ? (
                <Stack spacing={3}>
                  <SeatUsageBar
                    label="Executive Seats"
                    current={tenant.currentExecutives}
                    max={tenant.maxExecutiveSeats}
                  />
                  <SeatUsageBar
                    label="EA Seats"
                    current={tenant.currentEAs}
                    max={tenant.maxEaSeats}
                  />
                  <Divider />
                  <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>
                    Total active users: {tenant.totalUsers}
                  </Typography>
                </Stack>
              ) : !loading ? (
                <Typography variant="body2" sx={{ color: COLORS.textSecondary, py: 3, textAlign: 'center' }}>
                  Unable to load seat usage.
                </Typography>
              ) : null}
            </CardContent>
          </Card>
        </Grid>

        {/* Feature Flags Card */}
        <Grid size={{ xs: 12 }}>
          <Card sx={{ bgcolor: COLORS.bgPaper }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: COLORS.textPrimary, fontWeight: 600, mb: 2 }}>
                Plan Features
              </Typography>
              {tenant ? (
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Stack spacing={1}>
                      <FeatureFlag label="AI Planning" enabled={tenant.aiPlanningEnabled} />
                      <FeatureFlag label="Transcript Processing" enabled={tenant.transcriptProcessingEnabled} />
                      <FeatureFlag label="Image Analysis" enabled={tenant.imageAnalysisEnabled} />
                    </Stack>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Stack spacing={1}>
                      <FeatureFlag label="PDF Export" enabled={tenant.pdfExportEnabled} />
                      <FeatureFlag label="Webhooks" enabled={tenant.webhooksEnabled} />
                      <FeatureFlag label="API Access" enabled={tenant.apiAccessEnabled} />
                    </Stack>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <Stack spacing={1}>
                      <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>
                        Monthly AI Token Limit: <strong style={{ color: COLORS.textPrimary }}>{tenant.monthlyAiTokenLimit.toLocaleString()}</strong>
                      </Typography>
                      <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>
                        Monthly Transcript Limit: <strong style={{ color: COLORS.textPrimary }}>{tenant.monthlyTranscriptLimit}</strong>
                      </Typography>
                      <Typography variant="caption" sx={{ color: COLORS.textSecondary }}>
                        ({tenant.maxExecutiveSeats} executive{tenant.maxExecutiveSeats !== 1 ? 's' : ''} x 1.5/day x 30 days)
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              ) : !loading ? (
                <Typography variant="body2" sx={{ color: COLORS.textSecondary, py: 2, textAlign: 'center' }}>
                  Unable to load feature information.
                </Typography>
              ) : null}
            </CardContent>
          </Card>
        </Grid>

        {/* Users Table */}
        <Grid size={{ xs: 12 }}>
          <Card sx={{ bgcolor: COLORS.bgPaper }}>
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ color: COLORS.textPrimary, fontWeight: 600 }}>
                  Users
                </Typography>
              </Stack>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Last Login</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map(u => (
                      <TableRow key={u.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {u.displayName}
                            {u.id === user?.id && <Chip label="You" size="small" sx={{ ml: 1, height: 20 }} />}
                          </Typography>
                        </TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>
                          <Chip label={u.role} size="small" color={roleColor(u.role) as any} variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={u.isActive ? 'Active' : 'Inactive'}
                            size="small"
                            color={u.isActive ? 'success' : 'error'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString('en-GB') : 'Never'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(u.createdAt).toLocaleDateString('en-GB')}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            {u.id !== user?.id && (
                              <Tooltip title="Edit user">
                                <IconButton size="small" onClick={() => handleEditOpen(u)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Resend welcome email">
                              <IconButton size="small" onClick={() => handleResendWelcome(u.id)}>
                                <ResendIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                    {!loading && users.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography color="text.secondary" sx={{ py: 3 }}>No users found</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Data Export Card */}
        <Grid size={{ xs: 12 }}>
          <Card sx={{ bgcolor: COLORS.bgPaper }}>
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="h6" sx={{ color: COLORS.textPrimary, fontWeight: 600 }}>
                    Data Export
                  </Typography>
                  <Typography variant="body2" sx={{ color: COLORS.textSecondary, mt: 0.5 }}>
                    Download all your organisation's data as JSON (GDPR compliance).
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<ExportIcon />}
                  onClick={handleExport}
                  disabled={exportLoading}
                >
                  {exportLoading ? 'Exporting...' : 'Export Data'}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Edit User Dialog */}
      <Dialog open={!!editUser} onClose={() => setEditUser(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          {editUser && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>
                {editUser.displayName} ({editUser.email})
              </Typography>
              <TextField
                label="Role"
                select
                fullWidth
                value={editRole}
                onChange={e => setEditRole(e.target.value)}
              >
                {VALID_ROLES.map(r => (
                  <MenuItem key={r} value={r}>{r}</MenuItem>
                ))}
              </TextField>
              <TextField
                label="Status"
                select
                fullWidth
                value={editActive ? 'active' : 'inactive'}
                onChange={e => setEditActive(e.target.value === 'active')}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </TextField>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditUser(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave} disabled={editSaving}>
            {editSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
